/**
 * Appels d'authentification (ARCHITECTURE.md §5.1, specs §9.2).
 *
 * ⚠️ `startRegistration` et `startAuthentication` ouvrent l'invite système
 * (Face ID, Touch ID, code). Elles ne doivent **jamais** être appelées au
 * chargement : la spécification WebAuthn exige un geste utilisateur, et une
 * invite biométrique surgie sans qu'on l'ait demandée est un défaut d'usage.
 * Toutes les fonctions de ce fichier sont donc appelées depuis un gestionnaire
 * de clic, jamais depuis un effet.
 */

import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import type {
  AccountResponse,
  AuthStatusResponse,
  BootstrapResponse,
  PasskeySummary,
  RecoveryKeyResponse,
} from '../../../shared/types';
import { apiFetch } from './client';

function post<T>(path: string, body?: unknown): Promise<T> {
  return apiFetch<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

/** État d'authentification — c'est lui qui décide de l'écran affiché. */
export function fetchAuthStatus(): Promise<AuthStatusResponse> {
  return apiFetch<AuthStatusResponse>('/api/auth/status');
}

/**
 * Première configuration : enregistre la passkey initiale et renvoie la clé de
 * secours, **affichée une seule fois** (§9).
 */
export async function runBootstrap(
  token: string,
  deviceLabel: string,
): Promise<BootstrapResponse> {
  const optionsJSON = await post<PublicKeyCredentialCreationOptionsJSON>(
    '/api/auth/bootstrap/options',
    { token },
  );
  const response = await startRegistration({ optionsJSON });
  return post<BootstrapResponse>('/api/auth/bootstrap/verify', {
    token,
    deviceLabel,
    response,
  });
}

export async function loginWithPasskey(): Promise<AuthStatusResponse> {
  const optionsJSON = await post<PublicKeyCredentialRequestOptionsJSON>(
    '/api/auth/login/options',
  );
  const response = await startAuthentication({ optionsJSON });
  return post<AuthStatusResponse>('/api/auth/login/verify', { response });
}

export function loginWithRecoveryKey(recoveryKey: string): Promise<AuthStatusResponse> {
  return post<AuthStatusResponse>('/api/auth/recovery', { recoveryKey });
}

/** Ferme la session et renvoie l'état résultant, prêt à remplacer le cache. */
export function logout(): Promise<AuthStatusResponse> {
  return post<AuthStatusResponse>('/api/auth/logout');
}

export function fetchAccount(): Promise<AccountResponse> {
  return apiFetch<AccountResponse>('/api/auth/me');
}

/** Enregistre un appareil supplémentaire — iPhone **et** ordinateur (§4.2). */
export async function addPasskey(deviceLabel: string): Promise<PasskeySummary> {
  const optionsJSON = await post<PublicKeyCredentialCreationOptionsJSON>(
    '/api/auth/passkeys/options',
  );
  const response = await startRegistration({ optionsJSON });
  return post<PasskeySummary>('/api/auth/passkeys/verify', { deviceLabel, response });
}

export function removePasskey(id: number): Promise<void> {
  return apiFetch<void>(`/api/auth/passkeys/${String(id)}`, { method: 'DELETE' });
}

/** Renouvelle la clé de secours. L'ancienne cesse immédiatement de valoir. */
export function rotateRecoveryKey(): Promise<RecoveryKeyResponse> {
  return post<RecoveryKeyResponse>('/api/auth/recovery/rotate');
}

/**
 * Étiquette proposée pour l'appareil courant. Purement cosmétique : elle sert
 * à s'y retrouver dans la liste des passkeys des Réglages.
 */
export function guessDeviceLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) return 'Android';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'PC Windows';
  return 'Ordinateur';
}
